import React, { useState, useEffect } from "react";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { AiOutlineLogout, AiOutlineDashboard } from "react-icons/ai";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { IoIosNotifications } from "react-icons/io";
import { MdQuestionAnswer } from "react-icons/md";
import { BiSolidCalendarEvent } from "react-icons/bi";
import { Layout, Menu } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { io } from "socket.io-client";
import { getEnquiries } from "../features/enquiry/enquirySlice";
import { getBookings } from "../features/booking/bookingSlice";

const { Header, Sider, Content } = Layout;

const socket = io("http://localhost:8002", {
  withCredentials: true,
});

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation(); // ✅ ONLY DECLARE ONCE HERE

  useEffect(() => {
    dispatch(getEnquiries());
    dispatch(getBookings());

    socket.on("booking:new", () => {
      dispatch(getBookings());
      toast.success("New booking received");
    });

    socket.on("booking:updated", () => dispatch(getBookings()));
    socket.on("booking:deleted", () => dispatch(getBookings()));
    socket.on("booking:read", () => dispatch(getBookings()));

    socket.on("enquiry:new", () => {
      dispatch(getEnquiries());
      toast.info("New enquiry received");
    });

    socket.on("enquiry:updated", () => dispatch(getEnquiries()));
    socket.on("enquiry:deleted", () => dispatch(getEnquiries()));
    socket.on("enquiry:read", () => dispatch(getEnquiries()));

    return () => {
      socket.off("booking:new");
      socket.off("booking:updated");
      socket.off("booking:deleted");
      socket.off("booking:read");
      socket.off("enquiry:new");
      socket.off("enquiry:updated");
      socket.off("enquiry:deleted");
      socket.off("enquiry:read");
    };
  }, [dispatch]);

  const bookings = useSelector((state) => state?.booking?.bookings || []);
  const enquiries = useSelector((state) => state?.enquiry?.enquiries || []);

  const unreadEnquiriesCount = enquiries.filter((e) => !e.read).length;
  const unreadBookingsCount = bookings.filter((b) => !b.read).length;

  const carwashCount = bookings.filter(
    (b) => b.serviceType === "carwash" && !b.read
  ).length;

  const carpetCount = bookings.filter(
    (b) => b.serviceType === "carpet" && !b.read
  ).length;

  const waterCount = bookings.filter(
    (b) => b.serviceType === "water-refill" && !b.read
  ).length;

  const totalUnreadCount = unreadBookingsCount + unreadEnquiriesCount;

  const menuDot = (count) =>
    count > 0 ? <span className="menu-dot" /> : null;

  // ✅ Extract active route safely
  const currentKey = location.pathname.startsWith("/admin/")
    ? location.pathname.replace("/admin/", "")
    : "dashboard";

  return (
    <Layout className="admin-layout">
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        className="admin-sider"
      >
        <div className="admin-logo">
          {collapsed && <div className="logo-mark">S.S.H</div>}
          <span className="lg-logo">Savannah Shine Hub</span>
        </div>

        <div className="menu-label">Menu</div>

        <Menu
          theme="dark"
          className="menu-wrapper"
          mode="inline"
          selectedKeys={[currentKey]} // ✅ dynamic active menu
          onClick={({ key }) => {
            if (key === "signout") {
              localStorage.clear();
              window.location.reload();
            } else {
              navigate(`/admin/${key}`); // ✅ correct routing
            }
          }}
          items={[
            {
              key: "dashboard",
              icon: (
                <div className="menu-icon-wrap">
                  <AiOutlineDashboard />
                </div>
              ),
              label: "Dashboard",
            },
            {
              key: "carwash-bookings",
              icon: (
                <div className="menu-icon-wrap">
                  <BiSolidCalendarEvent />
                  {menuDot(carwashCount)}
                </div>
              ),
              label: "Carwash Bookings",
            },
            {
              key: "carpet-bookings",
              icon: (
                <div className="menu-icon-wrap">
                  <BiSolidCalendarEvent />
                  {menuDot(carpetCount)}
                </div>
              ),
              label: "Carpet Bookings",
            },
            {
              key: "water-refill",
              icon: (
                <div className="menu-icon-wrap">
                  <BiSolidCalendarEvent />
                  {menuDot(waterCount)}
                </div>
              ),
              label: "Water Refill",
            },
            {
              key: "enquiries",
              icon: (
                <div className="menu-icon-wrap">
                  <MdQuestionAnswer />
                  {menuDot(unreadEnquiriesCount)}
                </div>
              ),
              label: "Enquiries",
            },
            {
              key: "signout",
              icon: <AiOutlineLogout />,
              label: "Sign Out",
            },
          ]}
        />
      </Sider>

      <Layout className="site-layout">
        <Header className="admin-header">
          <button
            className="trigger-btn"
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </button>

          <div className="admin-header-right">
            <div className="notification-wrap">
              <IoIosNotifications className="notification-icon" />

              {totalUnreadCount > 0 && (
                <span className="notification-badge">
                  {totalUnreadCount}
                </span>
              )}
            </div>
          </div>
        </Header>

        <Content className="admin-content">
          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            pauseOnFocusLoss
            draggable
            theme="dark"
          />

          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;